import type { Supplier } from "@/apis/gql/types";
import { confColor } from "@/lib/miscellaneous/confidence-color";
import { groupSuppliersByParent } from "@/lib/miscellaneous/get-suppliers-by-parents";
import { cn } from "@/lib/utils";
import { useGetAllSuppliers } from "@/queries/suppliers/get-all";
import { useLocation } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { AutoSaveForm } from "@/components/shared/form/form-auto-save";
import { SupplierDropdownInput } from "@/components/shared/form/supplier-dropdown-input";
import { Form } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { useInvoiceReviewContext } from "../ReviewProvider";

type Props = {
  type: "creditor" | "debtor";
  disabled?: string | React.ReactNode | boolean;
  readOnly?: string | React.ReactNode | boolean;
  handleSupplierChange: (value: Supplier | null) => void;
};
export default function SupplierSelectField({
  type,
  disabled,
  readOnly,
  handleSupplierChange,
}: Props) {
  const { invoiceReviewState, updateBox } = useInvoiceReviewContext();
  const { invoiceData } = invoiceReviewState;
  const { data: suppliers } = useGetAllSuppliers({ type });
  const [rerenderKey, setForceRerenderKey] = useState(0);
  const location = useLocation();
  const [previousPath, setPreviousPath] = useState<string | null>(null);

  useEffect(() => {
    if (previousPath !== location.pathname) {
      setForceRerenderKey((prev) => prev + 1);

      if (invoiceData?.InvoicePolygon?.polygonData) {
        updateBox(invoiceData?.InvoicePolygon?.polygonData, "supplierName");
      }
    }
  }, [invoiceData?.invoiceId, suppliers]);

  useEffect(() => {
    setTimeout(() => setPreviousPath(location.pathname), 1000);
  }, [location.pathname]);

  let supplierConfidence = null;
  if (invoiceData?.supplierMatchConfidence == null) {
    supplierConfidence = null;
  } else if (invoiceData?.supplierMatchConfidence === 1) supplierConfidence = 1;
  else if (
    invoiceData?.supplier &&
    (invoiceData?.supplierMatchConfidence === 0 ||
      invoiceData?.supplier?.status !== "CONFIRMED")
  )
    supplierConfidence = 0;

  const form = useForm({
    values: {
      supplierId: invoiceData?.supplierId,
    },
  });

  return (
    <Form {...form}>
      <SupplierDropdownInput
        disabled={disabled}
        key={rerenderKey}
        name="supplierId"
        options={suppliers}
        className={cn("flex-1", confColor(supplierConfidence))}
        onBlur={() => {
          updateBox();
        }}
        onFocus={() => {
          updateBox(
            invoiceData?.invoicePolygon?.polygonData?.supplier,
            "supplierName",
          );
        }}
        readOnly={readOnly}
        type={type}
      />
      <AutoSaveForm onSubmit={handleSupplierChange} immediate />
    </Form>
  );
}
